/**
 * Core-100 R4 (OD-01 / OD-27): what a closure will do, in the words of the server's deletion catalogue.
 *
 * The lists come from the closure RPC's dry run (`close_my_account` / `close_shop_account` with p_dry_run), which
 * reads `privacy_data_classes` — the same catalogue the deletion engine executes. The App therefore cannot promise
 * more (or less) than the code does: a class whose treatment awaits a legal decision is shown as such, not as deleted.
 */
export interface ClosurePreview {
  deletion_due_at?: string;
  policy_days?: number;
  will_delete?: string[];
  pending_legal_decision?: string[];
  kept?: string[];
}

function List({ title, items, testId }: { title: string; items?: string[]; testId: string }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testId}>
      <p className="text-sm font-medium text-foreground">{title}</p>
      <ul className="list-disc pl-5 text-sm">
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </div>
  );
}

export function formatDeletionDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

export function ClosurePreviewLists({ preview, deleteTitle }: { preview: ClosurePreview; deleteTitle?: string }) {
  const due = formatDeletionDate(preview.deletion_due_at);
  return (
    <div className="space-y-3">
      <List
        testId="closure-will-delete"
        title={deleteTitle ?? `Deleted ${preview.policy_days ?? 30} days after closing${due ? ` (on or after ${due})` : ''}:`}
        items={preview.will_delete}
      />
      <List
        testId="closure-pending-decision"
        title="Not deleted yet — awaiting a legal decision on how the Privacy Policy applies:"
        items={preview.pending_legal_decision}
      />
      <List testId="closure-kept" title="Kept, under an exception in the Privacy Policy:" items={preview.kept} />
    </div>
  );
}
